import { useEffect, useState, useRef } from "react";
import { db } from "../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
// import { Search } from "@mui/icons-material";

const SearchProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "products"));
        const productList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productList);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Handle search filtering
  const handleSearch = (query) => {
    setSearchQuery(query);
    setHighlightIndex(-1); // Reset highlight when typing

    if (!query.trim()) {
      setFilteredProducts([]);
      setIsDropdownVisible(false);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = products.filter((product) =>
      [
        product.name,
        product.category,
        product.subcategory,
        product.brand,
        product.department,
      ].some((field) => field?.toLowerCase().includes(lowerQuery)),
    );

    setFilteredProducts(filtered.slice(0, 10)); // Limit to 10 results
    setIsDropdownVisible(filtered.length > 0);
  };

  // Handle search submission
  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
      setIsDropdownVisible(false);
      setSearchQuery("");
    }
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e) => {
    if (!filteredProducts.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredProducts.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredProducts.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex !== -1 && filteredProducts[highlightIndex]) {
        navigate(
          `/search?query=${encodeURIComponent(
            filteredProducts[highlightIndex].name,
          )}`,
        );
        setIsDropdownVisible(false);
        setSearchQuery("");
      } else if (searchQuery.trim()) {
        handleSearchSubmit();
      }
    } else if (e.key === "Escape") {
      setIsDropdownVisible(false);
      setHighlightIndex(-1);
    }
  };

  // Handle click on suggestion
  const handleSuggestionClick = (productName) => {
    navigate(`/search?query=${encodeURIComponent(productName)}`);
    setIsDropdownVisible(false);
    setSearchQuery("");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
        setHighlightIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative w-full">
      {/* Search Input */}
      <div className="flex">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search products..."
          className="w-full p-2 text-black border text-small border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coffee"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() =>
            filteredProducts.length > 0 && setIsDropdownVisible(true)
          }
          disabled={isLoading}
        />
        {/* <button
          onClick={handleSearchSubmit}
          disabled={isLoading || !searchQuery.trim()}
          className="p-2 bg-coffee rounded-r hidden lg:flex md:flex items-center justify-center text-white hover:bg-coffee transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search fontSize="small" />
        </button> */}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
          <p className="text-small text-gray-500">Loading products...</p>
        </div>
      )}

      {/* Dropdown for Suggestions */}
      {isDropdownVisible && filteredProducts.length > 0 && (
        <ul
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden max-h-80 overflow-y-auto"
        >
          {filteredProducts.map((product, index) => (
            <li
              key={product.id}
              className={`cursor-pointer border-b border-gray-100 last:border-b-0 ${
                highlightIndex === index
                  ? "bg-coffee text-white"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => handleSuggestionClick(product.name)}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              <div className="p-3 flex items-start space-x-3">
                {product.imageUrls && product.imageUrls[0] && (
                  <img
                    src={product.imageUrls[0]}
                    alt={product.name}
                    className="w-10 h-10 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-small font-medium truncate">
                    {product.name}
                  </p>
                  <p
                    className={`text-small capitalize ${
                      highlightIndex === index
                        ? "text-white"
                        : "text-gray-500"
                    }`}
                  >
                    {product.category} • {product.brand}
                  </p>
                  <p
                    className={`text-small ${
                      highlightIndex === index
                        ? "text-white"
                        : "text-gray-400"
                    }`}
                  >
                    GH₵ {product.price?.toLocaleString()}
                  </p>
                </div>
              </div>
            </li>
          ))}

          {/* Show all results link */}
          {filteredProducts.length > 0 && (
            <li className="border-t border-gray-200">
              <button
                onClick={handleSearchSubmit}
                className="w-full p-3 text-small text-center text-coffee hover:bg-lightBrown hover:text-coffeeBrown font-medium"
              >
                View all {filteredProducts.length} results for "{searchQuery}"
              </button>
            </li>
          )}
        </ul>
      )}

      {/* No results message */}
      {isDropdownVisible &&
        searchQuery.trim() &&
        filteredProducts.length === 0 &&
        !isLoading && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
            <p className="text-small text-gray-500">
              No products found for "{searchQuery}"
            </p>
          </div>
        )}
    </div>
  );
};

export default SearchProducts;
